import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import axios from 'axios';
import OwnerPageLayout from '../components/layout/OwnerPageLayout';
import SchoolLogoUpload from '../components/SchoolLogoUpload';
import ImportStudentsModal from '../components/ImportStudentsModal';
import ImportHistoryTable from '../components/ImportHistoryTable';
import { getApiBaseUrl } from '../apiBase';
import { getActiveSchoolId } from '../utils/activeSchool';
import AddressInput from '../components/address/AddressInput';
import { schoolGeoFromApi, profileGeoFields } from '../components/address/addressGeo';
import TopUpSmsModal from '../components/school/TopUpSmsModal';

const inputStyle = {
    width: '100%', padding: '12px 14px', borderRadius: '10px',
    border: '1.5px solid var(--sp-gray-200)', fontSize: '14px',
    outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit'
};

const sectionClass = 'sp-panel';
const sectionStyle = { marginBottom: '20px' };

export default function SchoolSettingsPage() {
    const { t } = useTranslation(['schoolSettings', 'school', 'sms', 'common', 'errors', 'orgSettings']);
    const [loading, setLoading] = useState(true);
    const [savingProfile, setSavingProfile] = useState(false);
    const [savingPayments, setSavingPayments] = useState(false);
    const [school, setSchool] = useState(null);
    const [paymentSettings, setPaymentSettings] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const [profile, setProfile] = useState({
        name: '', country: '', city: '', address: '', phone: '', inn: '',
        lat: null, lng: null, region: '', postalCode: '',
        defaultAssumeAttending: true,
        maxStudentsPerCoach: 30,
        hasCashDeskAndEquipment: false
    });
    const [yooForm, setYooForm] = useState({ shopId: '', secretKey: '' });
    const [importOpen, setImportOpen] = useState(false);
    const [importHistoryKey, setImportHistoryKey] = useState(0);
    const [smsBilling, setSmsBilling] = useState(null);
    const [smsV2Balance, setSmsV2Balance] = useState(null);
    const [smsPackages, setSmsPackages] = useState(null);
    const [smsTopUpOpen, setSmsTopUpOpen] = useState(false);
    const [smsSaving, setSmsSaving] = useState(false);

    const token = localStorage.getItem('token');
    const api = useMemo(() => axios.create({
        baseURL: getApiBaseUrl(),
        headers: { Authorization: `Bearer ${token}` }
    }), [token]);

    const webhookUrl = useMemo(() => {
        if (!school?.id) return '';
        const base = getApiBaseUrl().replace(/\/api\/?$/, '');
        return `${base}/api/schools/${school.id}/payments/webhook`;
    }, [school?.id]);

    const innLocked = school?.innLocked || !!school?.inn;
    const yooActive = paymentSettings?.isActive && paymentSettings?.hasSecretKey;
    const yooLocked = paymentSettings?.credentialsLocked === true;

    useEffect(() => {
        loadAll();
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('sms') !== 'success' || !school?.id) return;
        loadSmsV2Balance(school.id);
        const url = new URL(window.location.href);
        url.searchParams.delete('sms');
        window.history.replaceState({}, '', url.toString());
    }, [school?.id]);

    async function loadSmsV2Balance(schoolId) {
        if (!schoolId) return;
        try {
            const pkgRes = await api.get('/school/sms/balance', { params: { schoolId } });
            setSmsV2Balance(pkgRes.data);
            setSmsPackages(pkgRes.data);
        } catch {
            setSmsV2Balance(null);
        }
    }

    async function loadAll() {
        setLoading(true);
        setError('');
        try {
            const activeId = getActiveSchoolId();
            const schoolRes = await api.get('/school/my', {
                params: activeId ? { schoolId: activeId } : {}
            });
            const s = schoolRes.data;
            setSchool(s);
            const geo = schoolGeoFromApi(s);
            setProfile({
                name: s.name || '',
                country: s.country || t('schoolSettings:defaultCountry'),
                city: s.city || '',
                address: geo.address || s.address || '',
                phone: s.phone || '',
                inn: s.inn || '',
                ...profileGeoFields(geo),
                defaultAssumeAttending: s.defaultAssumeAttending !== false,
                maxStudentsPerCoach: s.maxStudentsPerCoach ?? 30,
                hasCashDeskAndEquipment: !!s.hasCashDeskAndEquipment
            });
            const payRes = await api.get(`/schools/${s.id}/payment-settings`);
            setPaymentSettings(payRes.data);
            try {
                const smsRes = await api.get(`/schools/${s.id}/sms-billing`);
                setSmsBilling(smsRes.data);
            } catch {
                setSmsBilling(null);
            }
            try {
                const pkgRes = await api.get('/school/sms/balance', { params: { schoolId: s.id } });
                setSmsV2Balance(pkgRes.data);
                setSmsPackages(pkgRes.data);
            } catch {
                setSmsV2Balance(null);
                setSmsPackages(null);
            }
        } catch (err) {
            if (err.response?.status === 404) {
                setError(t('schoolSettings:createSchoolFirst'));
            } else {
                setError(err.response?.data?.message || t('schoolSettings:loadError'));
            }
        }
        setLoading(false);
    }

    async function handleSaveProfile(e) {
        e.preventDefault();
        setSavingProfile(true);
        setError('');
        setSuccess('');
        try {
            const country = profile.country.trim();
            const city = profile.city.trim();
            if (country.length < 2) {
                setError(t('schoolSettings:countryMin'));
                setSavingProfile(false);
                return;
            }
            if (city.length < 2) {
                setError(t('schoolSettings:cityMin'));
                setSavingProfile(false);
                return;
            }
            const payload = {
                name: profile.name.trim(),
                sportTypeId: school.sportTypeId,
                country,
                city,
                address: profile.address.trim(),
                addressFull: profile.address.trim(),
                region: profile.region || null,
                postalCode: profile.postalCode || null,
                lat: profile.lat != null ? Number(profile.lat) : null,
                lng: profile.lng != null ? Number(profile.lng) : null,
                phone: profile.phone.trim(),
                inn: innLocked ? school.inn : (profile.inn.trim() || null),
                defaultAssumeAttending: profile.defaultAssumeAttending,
                maxStudentsPerCoach: Math.max(1, parseInt(String(profile.maxStudentsPerCoach), 10) || 30),
                hasCashDeskAndEquipment: !!profile.hasCashDeskAndEquipment
            };
            if (!innLocked && payload.inn) {
                const inn = payload.inn.replace(/\s/g, '');
                if (!/^\d{10}$/.test(inn) && !/^\d{12}$/.test(inn)) {
                    setError(t('schoolSettings:innFormat'));
                    setSavingProfile(false);
                    return;
                }
                payload.inn = inn;
            }
            const putRes = await api.put(`/school/${school.id}`, payload);
            let s = putRes.data;
            if (!s?.id) {
                const reload = await api.get('/school/my', { params: { schoolId: school.id } });
                s = reload.data;
            }
            setSchool(s);
            const geo = schoolGeoFromApi(s);
            setProfile({
                name: s.name || profile.name,
                country: s.country ?? profile.country,
                city: s.city ?? profile.city,
                address: geo.address || s.address || profile.address,
                phone: s.phone ?? profile.phone,
                inn: s.inn || profile.inn,
                ...profileGeoFields(geo),
                defaultAssumeAttending: s.defaultAssumeAttending === true,
                maxStudentsPerCoach: Number(s.maxStudentsPerCoach) > 0 ? Number(s.maxStudentsPerCoach) : 30,
                hasCashDeskAndEquipment: !!s.hasCashDeskAndEquipment
            });
            window.dispatchEvent(new Event('school-settings-updated'));
            setSuccess(t('schoolSettings:profileSaved'));
        } catch (err) {
            setError(err.response?.data?.message || t('schoolSettings:saveError'));
        }
        setSavingProfile(false);
    }

    async function handleSaveYooKassa(e) {
        e.preventDefault();
        if (yooLocked) return;
        setSavingPayments(true);
        setError('');
        setSuccess('');
        const shopId = yooForm.shopId.trim();
        const secretKey = yooForm.secretKey.trim();
        if (!shopId || !secretKey) {
            setError(t('schoolSettings:yookassaCredentials'));
            setSavingPayments(false);
            return;
        }
        try {
            const res = await api.post(`/schools/${school.id}/payment-settings`, { shopId, secretKey });
            setSuccess(t('schoolSettings:yookassaConnected', { id: res.data.shopIdMasked || 'OK' }));
            setYooForm(f => ({ ...f, secretKey: '' }));
            const payRes = await api.get(`/schools/${school.id}/payment-settings`);
            setPaymentSettings(payRes.data);
        } catch (err) {
            setError(err.response?.data?.message || t('schoolSettings:yookassaSaveError'));
        }
        setSavingPayments(false);
    }

    async function handleDisconnectYoo() {
        if (yooLocked) {
            alert(t('schoolSettings:yookassaCannotDisable'));
            return;
        }
        if (!window.confirm(t('schoolSettings:yookassaDisableConfirm'))) return;
        setSavingPayments(true);
        try {
            await api.delete(`/schools/${school.id}/payment-settings`);
            setSuccess(t('schoolSettings:yookassaDisabled'));
            setPaymentSettings({
                isActive: false, provider: 'yookassa',
                shopIdMasked: null, hasSecretKey: false, credentialsLocked: false
            });
            setYooForm({ shopId: '', secretKey: '' });
        } catch (err) {
            setError(err.response?.data?.message || t('schoolSettings:yookassaDisableError'));
        }
        setSavingPayments(false);
    }

    if (loading) {
        return (
            <OwnerPageLayout title={t('schoolSettings:title')} maxWidth={720}>
                <div className="sp-loading-page">{t('common:loading')}</div>
            </OwnerPageLayout>
        );
    }

    return (
        <OwnerPageLayout
            title={`⚙️ ${t('schoolSettings:title')}`}
            subtitle={t('schoolSettings:subtitle')}
            maxWidth={720}
            actions={(
                <a href="/school" className="sp-btn sp-btn--ghost" style={{ textDecoration: 'none' }}>
                    {t('schoolSettings:backToSchool')}
                </a>
            )}
        >
                {profile.hasCashDeskAndEquipment && (
                    <Link to="/school/settings/integrations" style={{
                        display: 'inline-block', marginBottom: 24, padding: '12px 16px',
                        background: '#eff6ff', borderRadius: 10, color: '#2563eb', fontWeight: 600, textDecoration: 'none'
                    }}>
                        {t('schoolSettings:integrationsLink')}
                    </Link>
                )}
                <Link to="/school/organization-settings" style={{
                    display: 'inline-block', marginBottom: 24, marginLeft: profile.hasCashDeskAndEquipment ? 12 : 0,
                    padding: '12px 16px', background: '#fff5f5', borderRadius: 10,
                    color: 'var(--sp-primary)', fontWeight: 600, textDecoration: 'none'
                }}>
                    {t('orgSettings:pageTitle')} →
                </Link>

                {error && (
                    <div style={{
                        background: '#fdecea', color: '#c0392b', padding: '12px 16px',
                        borderRadius: '10px', marginBottom: '16px', fontSize: '14px'
                    }}>
                        {error}
                    </div>
                )}
                {success && (
                    <div style={{
                        background: '#e8f5e9', color: '#2e7d32', padding: '12px 16px',
                        borderRadius: '10px', marginBottom: '16px', fontSize: '14px'
                    }}>
                        {success}
                    </div>
                )}

                {school && (
                    <>
                        <form onSubmit={handleSaveProfile} className={sectionClass}
                            style={sectionStyle}>
                            <h3 style={{ margin: '0 0 16px', fontSize: '16px' }}>{t('schoolSettings:mainData')}</h3>

                            <SchoolLogoUpload
                                schoolId={school.id}
                                logoPath={school.logo}
                                onUpdated={(logo) => setSchool(prev => ({ ...prev, logo }))}
                                onError={(msg) => setError(msg)}
                            />

                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                                    {t('schoolSettings:schoolName')}
                                </label>
                                <input
                                    value={profile.name}
                                    onChange={e => setProfile({ ...profile, name: e.target.value })}
                                    required
                                    style={inputStyle}
                                />
                            </div>

                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                                    {t('schoolSettings:sportType')}
                                </label>
                                <input
                                    value={school.sportType}
                                    disabled
                                    style={{ ...inputStyle, background: 'var(--sp-gray-50)', color: '#888' }}
                                />
                                <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
                                    {t('schoolSettings:sportTypeHint')}
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                                        {t('schoolSettings:country')}
                                    </label>
                                    <input
                                        value={profile.country}
                                        onChange={e => setProfile({ ...profile, country: e.target.value })}
                                        required
                                        minLength={2}
                                        style={inputStyle}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                                        {t('schoolSettings:city')}
                                    </label>
                                    <input
                                        value={profile.city}
                                        onChange={e => setProfile({ ...profile, city: e.target.value })}
                                        required
                                        minLength={2}
                                        placeholder={profile.city === t('schoolSettings:cityUnknown') ? t('schoolSettings:cityUnknownPlaceholder') : t('schoolSettings:cityPlaceholder')}
                                        style={inputStyle}
                                    />
                                    {profile.city === t('schoolSettings:cityUnknown') && (
                                        <div style={{ fontSize: '11px', color: '#c62828', marginTop: '4px' }}>
                                            {t('schoolSettings:cityStatsHint')}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                                    {t('schoolSettings:address')}
                                </label>
                                <AddressInput
                                    address={profile.address}
                                    lat={profile.lat}
                                    lng={profile.lng}
                                    city={profile.city}
                                    inputStyle={inputStyle}
                                    onChange={(v) => setProfile({
                                        ...profile,
                                        address: v.address || '',
                                        lat: v.lat ?? null,
                                        lng: v.lng ?? null,
                                        city: v.city || profile.city,
                                        region: v.region || profile.region,
                                        postalCode: v.postalCode || profile.postalCode
                                    })}
                                />
                            </div>

                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                                    {t('schoolSettings:phone')}
                                </label>
                                <input
                                    value={profile.phone}
                                    onChange={e => setProfile({ ...profile, phone: e.target.value })}
                                    style={inputStyle}
                                />
                            </div>

                            <div style={{
                                marginBottom: '20px', padding: '16px', borderRadius: '12px',
                                background: '#f8fafc', border: '1px solid #e2e8f0'
                            }}>
                                <h4 style={{ margin: '0 0 12px', fontSize: '14px' }}>{t('schoolSettings:plannedAttendance')}</h4>
                                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px', cursor: 'pointer', fontSize: '13px' }}>
                                    <input
                                        type="checkbox"
                                        checked={profile.defaultAssumeAttending}
                                        onChange={e => setProfile({ ...profile, defaultAssumeAttending: e.target.checked })}
                                        style={{ marginTop: '3px' }}
                                    />
                                    <span>
                                        {t('schoolSettings:assumeAttending')}
                                    </span>
                                </label>
                                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', fontSize: '13px' }}>
                                    <input
                                        type="checkbox"
                                        checked={!profile.defaultAssumeAttending}
                                        onChange={e => setProfile({ ...profile, defaultAssumeAttending: !e.target.checked })}
                                        style={{ marginTop: '3px' }}
                                    />
                                    <span>
                                        {t('schoolSettings:assumeNotAttending')}
                                    </span>
                                </label>
                                <div style={{ marginTop: '14px' }}>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                                        {t('schoolSettings:maxStudentsPerCoach')}
                                    </label>
                                    <input
                                        type="number"
                                        min={1}
                                        max={500}
                                        value={profile.maxStudentsPerCoach}
                                        onChange={e => setProfile({ ...profile, maxStudentsPerCoach: e.target.value })}
                                        style={{ ...inputStyle, maxWidth: '120px' }}
                                    />
                                    <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
                                        {t('schoolSettings:maxStudentsWarning')}
                                    </div>
                                </div>
                                <div style={{ marginTop: '18px', padding: '14px', background: '#f0f9ff', borderRadius: '10px', border: '1px solid #bae6fd' }}>
                                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', fontSize: '13px' }}>
                                        <input
                                            type="checkbox"
                                            checked={!!profile.hasCashDeskAndEquipment}
                                            onChange={e => setProfile({ ...profile, hasCashDeskAndEquipment: e.target.checked })}
                                            style={{ marginTop: '3px' }}
                                        />
                                        <span>
                                            <strong>{t('schoolSettings:cashDeskTitle')}</strong>
                                            <br />
                                            <span style={{ color: '#64748b' }}>
                                                {t('schoolSettings:cashDeskHint')}
                                            </span>
                                        </span>
                                    </label>
                                </div>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                                    {t('schoolSettings:innLabel')}{innLocked ? '' : t('schoolSettings:innOnce')}
                                </label>
                                {innLocked ? (
                                    <>
                                        <input value={school.inn} disabled style={{ ...inputStyle, background: 'var(--sp-gray-50)' }} />
                                        <div style={{ fontSize: '11px', color: '#888', marginTop: '6px' }}>
                                            {t('schoolSettings:innSupport')}
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <input
                                            value={profile.inn}
                                            onChange={e => setProfile({ ...profile, inn: e.target.value.replace(/\D/g, '').slice(0, 12) })}
                                            placeholder={t('schoolSettings:innPlaceholder')}
                                            inputMode="numeric"
                                            style={inputStyle}
                                        />
                                        <div style={{ fontSize: '11px', color: '#888', marginTop: '6px' }}>
                                            {t('schoolSettings:innLockedHint')}
                                        </div>
                                    </>
                                )}
                            </div>

                            <button
                                type="submit"
                                disabled={savingProfile}
                                style={{
                                    padding: '12px 24px', border: 'none', borderRadius: '10px',
                                    background: savingProfile ? '#94a3b8' : '#1A1A1A', color: 'white',
                                    fontWeight: '700', cursor: savingProfile ? 'not-allowed' : 'pointer'
                                }}
                            >
                                {savingProfile ? t('schoolSettings:savingProfile') : t('schoolSettings:saveProfile')}
                            </button>
                        </form>

                        <form onSubmit={handleSaveYooKassa} className={sectionClass}
                            style={sectionStyle}>
                            <h3 style={{ margin: '0 0 8px', fontSize: '16px' }}>{t('schoolSettings:yookassaTitle')}</h3>
                            <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#666', lineHeight: 1.5 }}>
                                {t('schoolSettings:yookassaHint')}{' '}
                                <a href="/billing" style={{ color: '#EB3333' }}>{t('schoolSettings:platformBillingLink')}</a>.
                            </p>

                            <div style={{
                                display: 'inline-block', marginBottom: '16px',
                                padding: '6px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: '700',
                                background: yooActive ? '#e8f5e9' : '#fff3e0',
                                color: yooActive ? '#2e7d32' : '#e65100'
                            }}>
                                {yooActive
                                    ? t('schoolSettings:yookassaOn', { id: paymentSettings.shopIdMasked })
                                    : t('schoolSettings:yookassaOff')}
                            </div>

                            {yooLocked && (
                                <div style={{
                                    background: '#fff8e1', border: '1px solid #ffe082',
                                    borderRadius: '10px', padding: '12px 14px', marginBottom: '16px',
                                    fontSize: '13px', color: '#6d4c00'
                                }}>
                                    {t('schoolSettings:yookassaLocked')}
                                </div>
                            )}

                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                                    {t('schoolSettings:shopId')}
                                </label>
                                <input
                                    value={yooForm.shopId}
                                    onChange={e => setYooForm({ ...yooForm, shopId: e.target.value.trim() })}
                                    placeholder="1363787"
                                    required={!yooLocked}
                                    disabled={yooLocked}
                                    style={{ ...inputStyle, ...(yooLocked ? { background: 'var(--sp-gray-50)' } : {}) }}
                                />
                            </div>

                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                                    {t('schoolSettings:secretKey')}
                                </label>
                                <input
                                    type="password"
                                    value={yooForm.secretKey}
                                    onChange={e => setYooForm({ ...yooForm, secretKey: e.target.value })}
                                    placeholder={t('schoolSettings:secretPlaceholder')}
                                    required={!yooLocked}
                                    disabled={yooLocked}
                                    autoComplete="off"
                                    style={{ ...inputStyle, ...(yooLocked ? { background: 'var(--sp-gray-50)' } : {}) }}
                                />
                                {!yooLocked && (
                                    <div style={{ fontSize: '11px', color: '#888', marginTop: '6px' }}>
                                        {t('schoolSettings:keyCheckHint')}
                                    </div>
                                )}
                            </div>

                            {!yooLocked && (
                                <button
                                    type="submit"
                                    disabled={savingPayments}
                                    style={{
                                        width: '100%', padding: '14px', border: 'none', borderRadius: '10px',
                                        background: savingPayments ? '#94a3b8' : '#EB3333', color: 'white',
                                        fontWeight: '700', cursor: savingPayments ? 'not-allowed' : 'pointer',
                                        marginBottom: '12px'
                                    }}
                                >
                                    {savingPayments ? t('schoolSettings:checking') : (yooActive ? t('schoolSettings:updateYookassa') : t('schoolSettings:connectYookassa'))}
                                </button>
                            )}

                            {yooActive && (
                                <>
                                    <div style={{ marginTop: '8px', marginBottom: '12px' }}>
                                        <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>Webhook</div>
                                        <code style={{
                                            display: 'block', padding: '12px', background: 'var(--sp-gray-50)',
                                            borderRadius: '8px', fontSize: '12px', wordBreak: 'break-all',
                                            border: '1px solid var(--sp-gray-200)'
                                        }}>
                                            {webhookUrl}
                                        </code>
                                        <div style={{ fontSize: '11px', color: '#888', marginTop: '8px' }}>
                                            {t('schoolSettings:webhookHint')}
                                        </div>
                                    </div>
                                    {!yooLocked && (
                                        <button
                                            type="button"
                                            onClick={handleDisconnectYoo}
                                            disabled={savingPayments}
                                            style={{
                                                padding: '10px 18px', borderRadius: '8px',
                                                border: '1px solid #ef9a9a', background: 'white',
                                                color: '#c62828', cursor: 'pointer', fontSize: '14px'
                                            }}
                                        >
                                            {t('schoolSettings:disconnectYookassa')}
                                        </button>
                                    )}
                                </>
                            )}
                        </form>
                    </>
                )}

                {school?.id && smsBilling && (
                    <div className={sectionClass}
                            style={sectionStyle} id="sms-section">
                        <h2 style={{ margin: '0 0 12px', fontSize: '18px' }}>{t('schoolSettings:smsTitle')}</h2>

                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                            gap: 16,
                            marginBottom: 16, padding: '14px 16px', background: 'var(--sp-gray-50)',
                            borderRadius: 12, border: '1px solid var(--sp-gray-200)'
                        }}>
                            <div>
                                <div style={{ fontSize: 13, color: '#64748b', marginBottom: 4 }}>
                                    {t('schoolSettings:smsPrepaidAccount', { defaultValue: 'Авансовый счёт' })}
                                </div>
                                <div style={{ fontSize: 22, fontWeight: 700, color: '#0f172a' }}>
                                    {new Intl.NumberFormat('ru-RU').format(smsBilling.balance)} ₽
                                    {smsBilling.balance < (smsBilling.tariff?.minimumDeposit ?? 0) && (
                                        <div style={{ fontSize: 13, fontWeight: 600, color: '#b45309', marginTop: 6 }}>
                                            ⚠️ {t('schoolSettings:smsLowDepositWarning', {
                                                min: new Intl.NumberFormat('ru-RU').format(smsBilling.tariff?.minimumDeposit ?? 0)
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {smsV2Balance && (
                                <div>
                                    <div style={{ fontSize: 13, color: '#64748b', marginBottom: 4 }}>
                                        {t('sms:balance', { defaultValue: 'Баланс SMS' })}
                                    </div>
                                    <div style={{ fontSize: 22, fontWeight: 700, color: '#0f172a' }}>
                                        {smsV2Balance.smsBalance} {t('sms:unitsAvailable', { defaultValue: 'шт. доступно' })}
                                    </div>
                                    {smsV2Balance.totalPurchased > 0 && (
                                        <div style={{ marginTop: 8 }}>
                                            <div style={{
                                                background: '#e5e7eb', borderRadius: 6, height: 8, overflow: 'hidden'
                                            }}>
                                                <div style={{
                                                    width: `${Math.min(100, Math.round((smsV2Balance.totalUsed / smsV2Balance.totalPurchased) * 100))}%`,
                                                    height: '100%',
                                                    background: smsV2Balance.smsBalance < 30 ? '#f59e0b' : '#10b981',
                                                    transition: 'width 0.3s'
                                                }} />
                                            </div>
                                            <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                                                {smsV2Balance.totalUsed} / {smsV2Balance.totalPurchased} {t('sms:used', { defaultValue: 'исп.' })}
                                            </div>
                                        </div>
                                    )}
                                    {smsV2Balance.smsBalance > 0 && smsV2Balance.smsBalance < 30 && (
                                        <div style={{ fontSize: 13, fontWeight: 600, color: '#b45309', marginTop: 6 }}>
                                            ⚠️ {t('sms:lowBalance', { defaultValue: 'Осталось мало SMS' })}
                                        </div>
                                    )}
                                    {smsV2Balance.isBlocked && (
                                        <div style={{ fontSize: 13, fontWeight: 600, color: '#dc2626', marginTop: 6 }}>
                                            🔴 {t('sms:blocked')}: {smsV2Balance.blockReason}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div style={{ marginBottom: 16 }}>
                            <button
                                type="button"
                                onClick={() => setSmsTopUpOpen(true)}
                                style={{
                                    padding: '12px 20px', borderRadius: 10, border: 'none',
                                    background: '#EB3333', color: '#fff', fontWeight: 600, cursor: 'pointer'
                                }}
                            >
                                {t('schoolSettings:smsTopUp')}
                            </button>
                        </div>

                        {smsBilling.tariff && (
                            <p style={{ fontSize: 14, color: '#64748b', marginBottom: 8 }}>
                                {t('schoolSettings:smsTariffLine', {
                                    pricePerSms: new Intl.NumberFormat('ru-RU').format(smsBilling.tariff.schoolPricePerMessage),
                                    monthlyFee: new Intl.NumberFormat('ru-RU').format(smsBilling.tariff.monthlySubscriptionFee)
                                })}
                            </p>
                        )}
                        <p style={{ fontSize: 14, color: '#64748b', marginBottom: 16 }}>
                            {t('schoolSettings:smsMinDeposit', {
                                min: new Intl.NumberFormat('ru-RU').format(smsBilling.tariff?.minimumDeposit ?? 0)
                            })}
                            {smsBilling.exampleFormula && (
                                <> · {t('schoolSettings:smsExample', { count: smsBilling.peopleCount, formula: smsBilling.exampleFormula })}</>
                            )}
                        </p>
                        <p style={{ fontSize: 13, color: '#EB3333', marginBottom: 16 }}>
                            {smsBilling.emailAlternative}
                        </p>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                            <input
                                type="checkbox"
                                checked={smsBilling.isServiceEnabled}
                                onChange={async (e) => {
                                    const enable = e.target.checked;
                                    if (enable && !window.confirm(
                                        t('schoolSettings:smsEnableConfirm', { warning: smsBilling.enableWarning })
                                    )) return;
                                    if (!enable && !window.confirm(t('schoolSettings:smsDisableConfirm'))) return;
                                    setSmsSaving(true);
                                    try {
                                        await api.put(`/schools/${school.id}/sms-billing`, {
                                            enableService: enable
                                        });
                                        await loadAll();
                                        setSuccess(e.target.checked ? t('schoolSettings:smsEnabled') : t('schoolSettings:smsDisabled'));
                                    } catch (err) {
                                        setError(err.response?.data?.message || t('schoolSettings:smsToggleFailed'));
                                    }
                                    setSmsSaving(false);
                                }}
                                disabled={smsSaving}
                            />
                            <span>{t('schoolSettings:smsEnableLabel')}</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <input
                                type="checkbox"
                                checked={smsBilling.preferEmailOnly}
                                onChange={async (e) => {
                                    setSmsSaving(true);
                                    try {
                                        await api.put(`/schools/${school.id}/sms-billing`, {
                                            preferEmailOnly: e.target.checked
                                        });
                                        await loadAll();
                                    } catch (err) {
                                        setError(err.response?.data?.message || t('common:error'));
                                    }
                                    setSmsSaving(false);
                                }}
                                disabled={smsSaving}
                            />
                            <span>{t('schoolSettings:smsEmailOnly')}</span>
                        </label>
                    </div>
                )}

                {school?.id && (
                    <TopUpSmsModal
                        open={smsTopUpOpen}
                        onClose={() => setSmsTopUpOpen(false)}
                        schoolId={school.id}
                        api={api}
                        tariffData={smsPackages}
                    />
                )}

                {school?.id && (
                    <div className={sectionClass}
                            style={sectionStyle}>
                        <h2 style={{ margin: '0 0 12px', fontSize: '18px' }}>{t('schoolSettings:importSectionTitle')}</h2>
                        <p style={{ color: '#64748b', fontSize: 14, marginBottom: 16 }}>
                            {t('schoolSettings:importSectionBody')}
                        </p>
                        <button
                            type="button"
                            onClick={() => setImportOpen(true)}
                            style={{
                                padding: '12px 20px', borderRadius: 10, border: 'none',
                                background: '#EB3333', color: '#fff', fontWeight: 600, cursor: 'pointer',
                                marginBottom: 20
                            }}
                        >
                            Импорт из Excel
                        </button>
                        <ImportHistoryTable
                            key={importHistoryKey}
                            schoolId={school.id}
                            api={api}
                        />
                    </div>
                )}

                {importOpen && school?.id && (
                    <ImportStudentsModal
                        schoolId={school.id}
                        api={api}
                        onClose={() => setImportOpen(false)}
                        onDone={() => setImportHistoryKey(k => k + 1)}
                    />
                )}
        </OwnerPageLayout>
    );
}
